import { isEmptyOrNil } from 'libs/empty'
import { trim, pick } from 'ramda'
import { getOrDefault } from 'libs/PagingParser'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

const replaceString = (text) => {
  return isEmptyOrNil(text) ? text : trim(text)
}

export const generalMappingFromApi = (value) => {
  return {
    firstName: getOrDefault('')(() => value.general.firstName),
    phoneticFirstName: getOrDefault('')(() => value.general.phoneticFirstName),
    middleName: getOrDefault('')(() => value.general.middleName),
    phoneticMiddleName: getOrDefault('')(
      () => value.general.phoneticMiddleName,
    ),
    lastName: getOrDefault('')(() => value.general.lastName),
    phoneticLastName: getOrDefault('')(() => value.general.phoneticLastName),
    formalTitle: getOrDefault('')(() => value.general.formalTitle),
    nickname: getOrDefault('')(() => value.general.nickname),
    dateOfBirth: getOrDefault('')(() => value.general.birthday),
    placeOfBirth: getOrDefault('')(() => value.general.placeOfBirth),
    prefix: getOrDefault('')(() => value.general.prefix),
    suffix: getOrDefault('')(() => value.general.suffix),
    gender: getOrDefault('')(() => value.general.gender),
    nationality: value.general.nationality,
    uniqueId: getOrDefault('')(() => value.general.uniqueId),
    passportId: getOrDefault('')(() => value.general.passportId),
    passportCountryCode: isEmptyOrNil(value.general.passportCountryCode)
      ? ''
      : parseInt(value.general.passportCountryCode),
    passportExpiresAt: getOrDefault('')(() => value.general.passportExpiresAt),
  }
}

export const generalMappingToApi = (value) => {
  return {
    firstName: replaceString(value.firstName),
    phoneticFirstName: replaceString(value.phoneticFirstName),
    middleName: replaceString(value.middleName),
    phoneticMiddleName: replaceString(value.phoneticMiddleName),
    lastName: replaceString(value.lastName),
    phoneticLastName: replaceString(value.phoneticLastName),
    formalTitle: replaceString(value.formalTitle),
    nickname: replaceString(value.nickname),
    birthday: isEmptyOrNil(value.dateOfBirth) ? null : value.dateOfBirth,
    placeOfBirth: replaceString(value.placeOfBirth),
    prefix: replaceString(value.prefix),
    suffix: replaceString(value.suffix),
    gender: value.gender,
    nationality: value.nationality,
    uniqueId: isEmptyOrNil(value.uniqueId)
      ? null
      : replaceString(value.uniqueId),
    passportId: replaceString(value.passportId),
    passportCountryCode: value.passportCountryCode,
    passportExpiresAt: isEmptyOrNil(value.passportExpiresAt)
      ? null
      : value.passportExpiresAt,
  }
}

export const contactMappingFromApi = (value) => {
  return {
    preferredChannel: getOrDefault(null)(
      () => value.receiveMethods.find((item) => item.isSelected) || null,
    ),
    email: getOrDefault('')(() => value.contact.email),
    phoneNumber: value.contact.phoneNumber
      ? `${value.contact.countryCode}${value.contact.phoneNumber}`
      : '',
    automatedCallPhoneNumber: value.contact.automatedCallPhoneNumber
      ? `${value.contact.automatedCallCountryCode}${value.contact.automatedCallPhoneNumber}`
      : '',
    contactable: getOrDefault(0)(() => value.contact.contactable),
    preferredLanguage: getOrDefault(null)(
      () => value.contact.language.languageId,
    ),
  }
}

export const contactMappingToApi = (value) => {
  const parsePhonNumber = parsePhoneNumberFromString(value.phoneNumber)
  const parseAutomateCall = parsePhoneNumberFromString(
    value.automatedCallPhoneNumber,
  )

  return {
    selectedChannel: pick(['id', 'channel', 'contact'], value.preferredChannel),
    email: replaceString(value.email),
    contactable: value.contactable,
    phoneNumber: isEmptyOrNil(parsePhonNumber)
      ? null
      : parsePhonNumber.nationalNumber,
    countryCode: isEmptyOrNil(parsePhonNumber)
      ? null
      : `+${parsePhonNumber.countryCallingCode}`,
    automatedCallPhoneNumber: isEmptyOrNil(parseAutomateCall)
      ? null
      : parseAutomateCall.nationalNumber,
    automatedCallCountryCode: isEmptyOrNil(parseAutomateCall)
      ? null
      : `+${parseAutomateCall.countryCallingCode}`,
    languageId: value.preferredLanguage,
  }
}

export const addressMappingFromApi = (value) => {
  return {
    physicalCountryCode: isEmptyOrNil(value.address.physicalCountryId)
      ? null
      : parseInt(value.address.physicalCountryId),
    physicalStateId: isEmptyOrNil(value.address.physicalStateId)
      ? null
      : parseInt(value.address.physicalStateId),

    physicalStreetAddress1: getOrDefault('')(
      () => value.address.physicalStreetAddress1,
    ),
    physicalStreetAddress2: getOrDefault('')(
      () => value.address.physicalStreetAddress2,
    ),
    physicalCityName: getOrDefault('')(() => value.address.physicalCityName),
    physicalProvinceName: getOrDefault('')(
      () => value.address.physicalProvinceName,
    ),

    physicalPostalCode: getOrDefault('')(
      () => value.address.physicalPostalCode,
    ),

    mailingCountryCode: isEmptyOrNil(value.address.mailingCountryId)
      ? ''
      : parseInt(value.address.mailingCountryId),
    mailingStateId: isEmptyOrNil(value.address.mailingStateId)
      ? null
      : parseInt(value.address.mailingStateId),

    mailingStreetAddress1: getOrDefault('')(
      () => value.address.mailingStreetAddress1,
    ),
    mailingStreetAddress2: getOrDefault('')(
      () => value.address.mailingStreetAddress2,
    ),
    mailingCityName: getOrDefault('')(() => value.address.mailingCityName),
    mailingPostalCode: getOrDefault('')(() => value.address.mailingPostalCode),
  }
}

export const addressMappingToApi = (value) => {
  return {
    physicalCountryId: isEmptyOrNil(value.physicalCountryCode)
      ? null
      : value.physicalCountryCode,
    physicalStateId: isEmptyOrNil(value.physicalStateId)
      ? null
      : value.physicalStateId,
    physicalCityName: replaceString(value.physicalCityName),
    physicalStreetAddress1: replaceString(value.physicalStreetAddress1),
    physicalStreetAddress2: replaceString(value.physicalStreetAddress2),
    physicalPostalCode: replaceString(value.physicalPostalCode),
    physicalProvinceName: replaceString(value.physicalProvinceName),
    mailingCountryId: isEmptyOrNil(value.mailingCountryCode)
      ? null
      : value.mailingCountryCode,
    mailingStateId: isEmptyOrNil(value.mailingStateId)
      ? null
      : parseInt(value.mailingStateId),
    mailingCityName: replaceString(value.mailingCityName),
    mailingStreetAddress1: replaceString(value.mailingStreetAddress1),
    mailingStreetAddress2: replaceString(value.mailingStreetAddress2),
    mailingPostalCode: replaceString(value.mailingPostalCode),
  }
}

export const employerMappingFromApi = (value) => {
  return {
    companyName: getOrDefault('')(() => value.employer.companyName),
    jobTitle: getOrDefault('')(() => value.employer.jobTitle),
    jobDepartment: getOrDefault('')(() => value.employer.jobDepartment),
  }
}

export const employerMappingToApi = (value) => {
  return {
    companyName: replaceString(value.companyName),
    jobTitle: replaceString(value.jobTitle),
    jobDepartment: replaceString(value.jobDepartment),
  }
}

export const socialMappingFromApi = (value) => {
  return {
    facebookProfile: getOrDefault('')(() => value.social.facebookProfile),
    lineProfile: getOrDefault('')(() => value.social.lineProfile),
    linkedinProfile: getOrDefault('')(() => value.social.linkedinProfile),
    twitterProfile: getOrDefault('')(() => value.social.twitterProfile),
    instagramProfile: getOrDefault('')(() => value.social.instagramProfile),
    ggProfile: getOrDefault('')(() => value.social.ggProfile),
    youtubeProfile: getOrDefault('')(() => value.social.youtubeProfile),
    otherProfile: getOrDefault('')(() => value.social.otherProfile),
  }
}

export const socialMappingToApi = (value) => {
  return {
    facebookProfile: replaceString(value.facebookProfile),
    lineProfile: replaceString(value.lineProfile),
    linkedinProfile: replaceString(value.linkedinProfile),
    twitterProfile: replaceString(value.twitterProfile),
    instagramProfile: replaceString(value.instagramProfile),
    ggProfile: replaceString(value.ggProfile),
    youtubeProfile: replaceString(value.youtubeProfile),
    otherProfile: replaceString(value.otherProfile),
  }
}
